import { cn } from '@skupreme/shared/ui';
import { CommonConstant } from '@utility/constants/common';
import { brand } from '@utility/config';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useState,
  type ImgHTMLAttributes,
  type ReactElement,
} from 'react';

/* Keeps the shown URL in step with `src`, and swaps in `fallback` once it fails. */
function useFallbackSrc(src: string | undefined, fallback: string) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setError] = useState(false);

  useEffect(() => {
    setImgSrc(src);
    setError(false);
  }, [src]);

  return {
    src: imgSrc || fallback,
    hasError,
    onError: () => {
      setImgSrc(fallback);
      setError(true);
    },
  };
}

interface IImageWithFallbackProps extends ImgHTMLAttributes<HTMLImageElement> {
  fallback?: string;
  src?: string;
}

export function ImageWithFallback({
  fallback = CommonConstant.defaultImage,
  src,
  ...rest
}: IImageWithFallbackProps) {
  const image = useFallbackSrc(src, fallback);

  return (
    <img alt={brand.name} src={image.src} onError={image.onError} {...rest} />
  );
}

export type SemanticImageSize =
  | 'mini'
  | 'tiny'
  | 'small'
  | 'medium'
  | 'large'
  | 'big'
  | 'huge'
  | 'massive';

/* The widths Semantic's `.ui.<size>.image` gave, which callers still lay out around. */
const SEMANTIC_WIDTHS: Record<SemanticImageSize, string> = {
  mini: 'sk:w-[35px]',
  tiny: 'sk:w-20',
  small: 'sk:w-[150px]',
  medium: 'sk:w-[300px]',
  large: 'sk:w-[450px]',
  big: 'sk:w-[600px]',
  huge: 'sk:w-[800px]',
  massive: 'sk:w-[960px]',
};

export interface ISemanticImageWithFallbackProps
  extends ImgHTMLAttributes<HTMLImageElement> {
  fallback?: string;
  src?: string;
  size?: SemanticImageSize;
  avatar?: boolean;
  bordered?: boolean;
  centered?: boolean;
  circular?: boolean;
  disabled?: boolean;
  fluid?: boolean;
  hidden?: boolean;
  inline?: boolean;
  rounded?: boolean;
  spaced?: boolean | 'left' | 'right';
  floated?: 'left' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

/** An `<img>` taking the props Semantic's `Image` took, sized the way it sized them. */
export function SemanticImageWithFallback({
  fallback = CommonConstant.defaultImage,
  src,
  size,
  avatar,
  bordered,
  centered,
  circular,
  disabled,
  fluid,
  hidden,
  inline,
  rounded,
  spaced,
  floated,
  verticalAlign = 'middle',
  className,
  ...rest
}: ISemanticImageWithFallbackProps) {
  const image = useFallbackSrc(src, fallback);

  return (
    <img
      alt={brand.name}
      {...rest}
      src={image.src}
      onError={image.onError}
      className={cn(
        'sk:h-auto sk:max-w-full',
        centered ? 'sk:mx-auto sk:block' : 'sk:inline-block',
        verticalAlign === 'top' && 'sk:align-top',
        verticalAlign === 'middle' && 'sk:align-middle',
        verticalAlign === 'bottom' && 'sk:align-bottom',
        size && SEMANTIC_WIDTHS[size],
        fluid && 'sk:block sk:w-full',
        avatar && 'sk:mr-1 sk:size-8 sk:rounded-full sk:object-cover',
        rounded && 'sk:rounded-option',
        circular && 'sk:rounded-full',
        bordered && 'sk:inset-shadow-hairline',
        disabled && 'sk:opacity-45',
        hidden && 'sk:hidden',
        inline && 'sk:inline-block',
        (spaced === true || spaced === 'left') && 'sk:ml-2',
        (spaced === true || spaced === 'right') && 'sk:mr-2',
        floated === 'left' && 'sk:float-left sk:mr-4',
        floated === 'right' && 'sk:float-right sk:ml-4',
        className,
      )}
    />
  );
}

interface IImageWithCustomFallbackProps
  extends ImgHTMLAttributes<HTMLImageElement> {
  fallback?: string;
  src?: string;
  fallbackComponent?: ReactElement;
}

export function ImageWithCustomFallback({
  fallback = CommonConstant.defaultImage,
  src,
  fallbackComponent,
  ...rest
}: IImageWithCustomFallbackProps) {
  const image = useFallbackSrc(src, fallback);

  if (image.hasError && isValidElement(fallbackComponent)) {
    return cloneElement(fallbackComponent);
  }

  return (
    <img alt={brand.name} src={image.src} onError={image.onError} {...rest} />
  );
}
